import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, TRAMITES, TRAMITE_LIST } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Users, BarChart3, Plus, LogOut, Search, CircleDollarSign } from "lucide-react";

const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_gestor-clientes-nss/artifacts/zdemw092_ChatGPT%20Image%2019%20sept%202026%2C%2005_05_17%20p.m..webp";
import ClientRegisterModal from "@/components/ClientRegisterModal";
import ClientDetailModal from "@/components/ClientDetailModal";
import ClientCard from "@/components/ClientCard";
import Statistics from "@/components/Statistics";
import Cobranza from "@/components/Cobranza";

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [section, setSection] = useState("portafolio");
  const [tramiteTab, setTramiteTab] = useState("TODAS");
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchMe = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      navigate("/", { replace: true });
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tramiteTab !== "TODAS") params.tramite = tramiteTab;
      if (q) params.q = q;
      const r = await api.get("/clients", { params });
      setClients(r.data);
    } catch (e) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  useEffect(() => {
    if (user) fetchClients();
    // eslint-disable-next-line
  }, [user, tramiteTab, q]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    navigate("/", { replace: true });
  };

  const counts = useMemo(() => {
    const c = { TODAS: clients.length };
    for (const t of TRAMITE_LIST) c[t.key] = 0;
    for (const cl of clients) if (cl.tramite && c[cl.tramite] !== undefined) c[cl.tramite]++;
    return c;
  }, [clients]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-500 text-sm animate-pulse">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-black text-white border-b border-yellow-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Luis Pensiones" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-heading font-bold text-yellow-400 leading-none">Luis Pensiones</div>
              <div className="text-[11px] text-neutral-300">Panel del asesor</div>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 rounded-full bg-neutral-900 border border-yellow-500/20 p-1">
            <button
              data-testid="nav-portafolio"
              onClick={() => setSection("portafolio")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${section === "portafolio" ? "bg-yellow-400 text-black shadow" : "text-neutral-300 hover:text-yellow-400"}`}
            >
              <Users className="w-4 h-4 inline mr-1.5" />Portafolio
            </button>
            <button
              data-testid="nav-cobranza"
              onClick={() => setSection("cobranza")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${section === "cobranza" ? "bg-yellow-400 text-black shadow" : "text-neutral-300 hover:text-yellow-400"}`}
            >
              <CircleDollarSign className="w-4 h-4 inline mr-1.5" />Cobranza
            </button>
            <button
              data-testid="nav-estadisticas"
              onClick={() => setSection("estadisticas")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${section === "estadisticas" ? "bg-yellow-400 text-black shadow" : "text-neutral-300 hover:text-yellow-400"}`}
            >
              <BarChart3 className="w-4 h-4 inline mr-1.5" />Estadísticas
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <Avatar className="ring-2 ring-yellow-500/50">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-yellow-400 text-black">{(user.name || user.email || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button" title="Cerrar sesión" className="text-neutral-200 hover:text-yellow-400 hover:bg-neutral-900">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-yellow-500/20 flex bg-neutral-950">
          <button
            onClick={() => setSection("portafolio")}
            className={`flex-1 py-2.5 text-sm font-medium ${section === "portafolio" ? "text-yellow-400 border-b-2 border-yellow-400" : "text-neutral-400"}`}
          >Portafolio</button>
          <button
            onClick={() => setSection("cobranza")}
            className={`flex-1 py-2.5 text-sm font-medium ${section === "cobranza" ? "text-yellow-400 border-b-2 border-yellow-400" : "text-neutral-400"}`}
          >Cobranza</button>
          <button
            onClick={() => setSection("estadisticas")}
            className={`flex-1 py-2.5 text-sm font-medium ${section === "estadisticas" ? "text-yellow-400 border-b-2 border-yellow-400" : "text-neutral-400"}`}
          >Estadísticas</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {section === "portafolio" ? (
          <section data-testid="portafolio-section">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-black">Portafolio</h1>
                <p className="text-neutral-500 mt-1 text-sm">Gestiona los trámites de tus clientes en un solo lugar.</p>
              </div>
              <Button
                data-testid="open-register-button"
                onClick={() => setRegisterOpen(true)}
                className="bg-black hover:bg-neutral-900 text-yellow-400 h-11 px-5 rounded-xl shadow border border-yellow-500/40"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Registrar cliente
              </Button>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por Nombre, NSS, CURP o Email"
                  className="pl-9 h-11 bg-white"
                />
              </div>
            </div>

            <Tabs value={tramiteTab} onValueChange={setTramiteTab}>
              <TabsList className="bg-white border border-neutral-200 h-auto flex-wrap p-1 gap-1">
                <TabsTrigger value="TODAS" data-testid="tab-TODAS" className="data-[state=active]:bg-black data-[state=active]:text-yellow-400 rounded-lg">
                  Todas <span className="ml-2 text-xs opacity-70">{counts.TODAS}</span>
                </TabsTrigger>
                {TRAMITE_LIST.map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    data-testid={`tab-${t.key}`}
                    className="rounded-lg data-[state=active]:text-white"
                    style={
                      tramiteTab === t.key
                        ? { backgroundColor: t.color, color: "#fff" }
                        : {}
                    }
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${t.dot}`} />
                    {t.label}
                    <span className="ml-2 text-xs opacity-70">{counts[t.key] || 0}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <div className="mt-6 grid gap-3">
              {loading && <div className="text-neutral-500 text-sm">Cargando…</div>}
              {!loading && clients.length === 0 && (
                <div className="p-10 text-center bg-white border border-dashed border-neutral-300 rounded-2xl">
                  <Users className="w-8 h-8 text-neutral-400 mx-auto" />
                  <p className="mt-3 text-neutral-500 text-sm">Aún no hay clientes en esta pestaña.</p>
                  <Button onClick={() => setRegisterOpen(true)} className="mt-4 bg-black hover:bg-neutral-900 text-yellow-400 border border-yellow-500/40">
                    Registrar el primero
                  </Button>
                </div>
              )}
              {clients.map((c) => (
                <ClientCard key={c.id} client={c} onOpen={() => setSelectedId(c.id)} />
              ))}
            </div>
          </section>
        ) : section === "cobranza" ? (
          <Cobranza />
        ) : (
          <Statistics />
        )}
      </main>

      <ClientRegisterModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onCreated={() => {
          setRegisterOpen(false);
          fetchClients();
          toast.success("Cliente registrado");
        }}
      />
      {selectedId && (
        <ClientDetailModal
          clientId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={fetchClients}
        />
      )}
    </div>
  );
}
